/**
 * Crear contraseña o PIN (Alta.dc.html, pantalla 5). Dos caras:
 *
 * - /restablecer: reset normal ("Nueva contraseña"). Siempre contraseña.
 * - /invitacion: un trabajador acepta la invitación de su empresa — el alta ya
 *   se hizo con el formulario completo (nombre incluido), así que aquí solo
 *   saluda por nombre+empresa y pide la credencial que toque.
 *
 * Sin correo no hay contraseña —una contraseña necesita un nombre de usuario
 * al lado, y aquí no lo hay—: la credencial es el PIN, el mismo que usa el
 * quiosco y la recuperación de acceso con el QR del centro. `getInvitation`
 * ya dice `requires_pin` en la misma llamada que usa para saludar por
 * nombre, así que no hace falta ninguna pantalla nueva: es esta misma, con
 * lo que toque. Con medidor de fuerza para la contraseña (no aplica al PIN).
 * Un token caducado no es un callejón: lleva a /recuperar.
 *
 * El PIN NO se teclea aquí: lo genera el servidor y esta pantalla lo enseña
 * una sola vez. Que lo eligiera el trabajador obligaba al backend a decirle
 * cuándo chocaba con el de un compañero, y ese mensaje —en una pantalla
 * pública— revelaba qué PIN están en uso en la empresa.
 */
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { activatePin, getInvitation, resetPassword } from '../../services/apiClient';
import { AuthLayout, CARD_SHADOW, MessageCard } from './AuthLayout';
import { PasswordField } from '../ui/PasswordField';

const STRENGTH = [
  { pct: '0%', color: 'var(--ink-3)', label: 'Escribe una contraseña de 8+ caracteres' },
  { pct: '33%', color: 'var(--signal)', label: 'Corta: mejor 8 caracteres o más' },
  { pct: '66%', color: 'var(--warn)', label: 'Aceptable' },
  { pct: '100%', color: 'var(--brand)', label: 'Buena contraseña' },
];

export function RestablecerPage({ invite = false }: { invite?: boolean }) {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [invited, setInvited] = useState<
    { first_name: string; company_name: string; requires_pin: boolean } | null
  >(null);
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  // El PIN que ha asignado el servidor: sólo existe aquí y sólo esta vez.
  const [pinCreado, setPinCreado] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [tokenDead, setTokenDead] = useState(false);
  // La invitación no se pudo consultar por algo que NO es un 404 (un 429 con
  // la cuadrilla entera activándose desde el wifi de la obra, un corte de
  // red...). En la duda no se adivina: sin saber si esta cuenta va por PIN o
  // por contraseña, enseñar un formulario es enseñar el equivocado.
  const [cargaFallida, setCargaFallida] = useState(false);

  const strength = STRENGTH[pw.length === 0 ? 0 : pw.length < 8 ? 1 : pw.length < 12 ? 2 : 3];
  // Sólo tiene sentido dentro de /invitacion: /restablecer es siempre
  // contraseña (nadie sin correo pide "olvidé mi contraseña").
  const requiresPin = invite && invited?.requires_pin === true;

  const [intento, setIntento] = useState(0);

  useEffect(() => {
    if (!invite || !token) return;
    setCargaFallida(false);
    getInvitation(token)
      .then(setInvited)
      .catch((err: unknown) => {
        const apiError = (err as { apiError?: { status?: number } }).apiError;
        if (apiError?.status === 404) setTokenDead(true);
        else setCargaFallida(true);
      });
  }, [invite, token, intento]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (requiresPin) {
      setBusy(true);
      try {
        const { pin } = await activatePin({ token });
        setPinCreado(pin);
        setDone(true);
      } catch (err: unknown) {
        const apiError = (err as { apiError?: { status: number; message: string } }).apiError;
        // 400 = enlace muerto (mismo criterio que el camino de contraseña).
        // Cualquier otro (503 si la empresa no tiene ningún PIN libre) NO es
        // un callejón: la invitación sigue viva y se puede reintentar, así
        // que se enseña el mensaje del backend tal cual.
        if (apiError?.status === 400) setTokenDead(true);
        else setError(apiError?.message ?? 'No se ha podido guardar. Inténtalo de nuevo.');
      } finally {
        setBusy(false);
      }
      return;
    }

    if (pw !== pw2) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    setBusy(true);
    try {
      await resetPassword({ token, new_password: pw });
      setDone(true);
    } catch (err: unknown) {
      const apiError = (err as { apiError?: { status: number; message: string } }).apiError;
      if (apiError?.status === 400) setTokenDead(true);
      else setError(apiError?.message ?? 'No se ha podido guardar. Inténtalo de nuevo.');
    } finally {
      setBusy(false);
    }
  };

  if (tokenDead) {
    return (
      <AuthLayout>
        <MessageCard
          icon="alert"
          tone="signal"
          title="Este enlace ya no sirve"
          body={
            invite
              ? 'La invitación ha caducado o ya se usó. Pide a tu empresa que te envíe otra.'
              : 'El enlace ha caducado o ya se usó. Pide uno nuevo y llega antes de 1 hora.'
          }
        >
          {!invite && (
            <Link to="/recuperar" className="btn-primary grid place-items-center no-underline">
              Pedir un enlace nuevo
            </Link>
          )}
        </MessageCard>
      </AuthLayout>
    );
  }

  // M-9: sin saber si esta cuenta va por PIN o por contraseña, cualquier
  // formulario que pintemos puede ser el equivocado. Se dice lo que pasa y se
  // ofrece reintentar, en vez de adivinar.
  if (cargaFallida) {
    return (
      <AuthLayout>
        <MessageCard
          icon="alert"
          tone="signal"
          title="No se ha podido cargar tu invitación"
          body="Puede ser la conexión o que se hayan hecho muchas activaciones seguidas desde esta obra. Espera un momento y vuelve a intentarlo: tu invitación sigue siendo válida."
        >
          <button onClick={() => setIntento((n) => n + 1)} className="btn-primary">
            Reintentar
          </button>
        </MessageCard>
      </AuthLayout>
    );
  }

  // Mientras la invitación no ha llegado no se pinta ningún formulario: el de
  // contraseña era el que salía por defecto, y a quien va por PIN le pedía
  // algo que no tiene.
  if (invite && token && !invited) {
    return (
      <AuthLayout>
        <MessageCard icon="shield" title="Un momento" body="Estamos comprobando tu invitación." />
      </AuthLayout>
    );
  }

  if (done) {
    return (
      <AuthLayout>
        <MessageCard
          icon="check-circle"
          title={invite ? 'Ya eres del equipo' : 'Contraseña actualizada'}
          body={
            requiresPin
              ? 'Este es tu PIN. Apúntalo o memorízalo ahora: no se vuelve a enseñar.'
              : invite
                ? 'Tu contraseña está creada. Entra y empieza a fichar desde el móvil o el kiosco.'
                : 'Ya puedes iniciar sesión con la contraseña nueva.'
          }
        >
          {requiresPin ? (
            <div className="flex flex-col gap-3">
              <div className="rounded-2xl border border-line-strong bg-surface px-4 py-5">
                <span className="font-mono font-bold text-[40px] tracking-[0.35em] text-ink">
                  {pinCreado}
                </span>
              </div>
              <p className="rounded-xl bg-warn-soft text-warn px-4 py-3 text-[13.5px] text-left">
                Es tu llave: con él fichas en la tablet de la entrada y, si pierdes el
                móvil, recuperas el acceso escaneando el QR del centro. No se lo des a
                nadie y no vuelvas a verlo aquí.
              </p>
              {/* Para quien no tiene correo, /login no sirve de nada —no hay
                  usuario que escribir—: el destino es la pantalla que entra
                  con el QR del centro y este mismo PIN. */}
              <Link
                to="/recuperar-acceso"
                className="btn-primary grid place-items-center no-underline"
              >
                Ya lo tengo apuntado
              </Link>
            </div>
          ) : (
            <Link to="/login" className="btn-primary grid place-items-center no-underline">
              {invite ? 'Entrar en Kakuat' : 'Iniciar sesión'}
            </Link>
          )}
        </MessageCard>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div
        className="w-full max-w-[520px] self-start bg-canvas border border-line-strong rounded-3xl p-7 sm:p-11 animate-rise"
        style={CARD_SHADOW}
      >
        {invite && invited && (
          <div className="inline-flex items-center gap-2 bg-brand-soft border border-line-strong rounded-full px-3.5 py-[7px] mb-4">
            <span className="font-semibold text-[13.5px] text-brand">
              Hola {invited.first_name} · {invited.company_name}
            </span>
          </div>
        )}
        <h1 className="font-display font-extrabold text-[26px] sm:text-[32px] tracking-tight mb-2.5">
          {requiresPin ? 'Crea tu PIN' : invite ? 'Crea tu contraseña' : 'Nueva contraseña'}
        </h1>
        <p className="text-ink-2 text-[15.5px] leading-relaxed mb-6">
          {requiresPin
            ? 'Sin correo, el PIN es tu credencial: lo usarás en el quiosco y, si pierdes el móvil, para recuperar el acceso escaneando el QR del centro. Lo elige Kakuat por ti y te lo enseña en la pantalla siguiente, una sola vez.'
            : invite
              ? 'Elige una contraseña para entrar en Kakuat y empezar a fichar.'
              : 'Elige una contraseña nueva. La usarás para entrar a partir de ahora.'}
        </p>

        {error && (
          <p className="rounded-xl bg-danger-soft text-danger px-4 py-3 text-[14px] mb-4">{error}</p>
        )}

        <form onSubmit={submit} className="flex flex-col gap-4">
          {requiresPin ? (
            <p className="rounded-xl border border-line-strong bg-surface px-4 py-3.5 text-[13.5px] text-ink-2">
              Al continuar te asignamos un PIN de cuatro cifras. Tenlo a mano: la
              pantalla siguiente es la única vez que se muestra.
            </p>
          ) : (
            <>
              <label className="flex flex-col gap-1.5">
                <span className="label !mb-0">Nueva contraseña</span>
                <PasswordField inputClassName="field bg-surface" placeholder="Mínimo 8 caracteres" required minLength={8} maxLength={128} autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} />
              </label>
              <div>
                <div className="h-1.5 rounded-full bg-surface-3 overflow-hidden">
                  <div className="h-full transition-all duration-300" style={{ width: strength.pct, background: strength.color }} />
                </div>
                <span className="text-[12.5px] inline-block mt-1.5" style={{ color: strength.color }}>
                  {strength.label}
                </span>
              </div>
              <label className="flex flex-col gap-1.5">
                <span className="label !mb-0">Repite la contraseña</span>
                <PasswordField inputClassName="field bg-surface" placeholder="Otra vez" required autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} />
              </label>
            </>
          )}

          <button type="submit" disabled={busy} className="btn-primary">
            {busy ? 'Guardando…' : requiresPin ? 'Crear mi PIN' : invite ? 'Entrar en Kakuat' : 'Guardar contraseña'}
          </button>
        </form>
      </div>
    </AuthLayout>
  );
}
